/**
 * StatsMonitorWidget — Shows status of the ABET Stats Monitor CF Worker
 * Data: GET https://abet-stats-monitor.abetco.workers.dev/status
 * CSS prefix: smw-
 */

import { useState, useEffect, useCallback } from 'react'
import { Activity, RefreshCw, ChevronRight, CheckCircle, AlertTriangle, XCircle } from 'lucide-react'
import './StatsMonitorWidget.css'

const STATS_URL = 'https://abet-stats-monitor.abetco.workers.dev'

function StatsMonitorWidget({ sportFilter }) {
  // sportFilter: 'mlb' = show only MLB, undefined = show all
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [checkResult, setCheckResult] = useState(null)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await fetch(`${STATS_URL}/status`, { signal: AbortSignal.timeout(10000) })
      const data = await resp.json()
      setStatus(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 120000) // 2 min refresh
    return () => clearInterval(interval)
  }, [fetchStatus])

  const triggerCheck = async () => {
    setChecking(true)
    setCheckResult(null)
    try {
      const resp = await fetch(`${STATS_URL}/check`, { signal: AbortSignal.timeout(25000) })
      const data = await resp.json()
      setCheckResult(data)
      // Also refresh status
      fetchStatus()
    } catch (e) {
      setCheckResult({ error: e.message })
    }
    setChecking(false)
  }

  const lastResults = status?.last_results
  const lastCheck = status?.last_check

  const timeSinceCheck = lastCheck
    ? Math.round((Date.now() - new Date(lastCheck).getTime()) / 60000)
    : null

  return (
    <div className="widget-card">
      <div
        className="widget-header smw-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <Activity size={16} className="widget-icon" />
        <span className="widget-title">Stats Monitor</span>
        {lastCheck && (
          <span className={`status-badge ${timeSinceCheck > 150 ? 'warning' : 'ok'}`} style={{ fontSize: 9 }}>
            {timeSinceCheck != null ? `${timeSinceCheck}m ago` : '—'}
          </span>
        )}
        <ChevronRight
          size={16}
          className={`smw-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Last results summary */}
          {lastResults && (
            <div className="smw-summary">
              {(!sportFilter || sportFilter === 'nfl') && (
              <div className="smw-monitor-row">
                <span className="smw-sport-icon">🏈</span>
                <span className="smw-sport-label">NFLverse</span>
                <span className="smw-sport-value">
                  {lastResults.nfl_new_releases > 0 ? (
                    <span className="smw-new">{lastResults.nfl_new_releases} new release{lastResults.nfl_new_releases !== 1 ? 's' : ''}</span>
                  ) : (
                    <span className="smw-none">No changes</span>
                  )}
                </span>
                {lastResults.nfl_errors?.length > 0 && (
                  <AlertTriangle size={12} className="smw-warn-icon" />
                )}
              </div>
              )}

              {(!sportFilter || sportFilter === 'nba') && (
              <div className="smw-monitor-row">
                <span className="smw-sport-icon">🏀</span>
                <span className="smw-sport-label">NBA</span>
                <span className="smw-sport-value">
                  {lastResults.nba_new_games > 0 ? (
                    <span className="smw-new">{lastResults.nba_new_games} completed</span>
                  ) : (
                    <span className="smw-none">No new finals</span>
                  )}
                </span>
                {lastResults.nba_errors?.length > 0 && (
                  <AlertTriangle size={12} className="smw-warn-icon" />
                )}
              </div>
              )}

              {(!sportFilter || sportFilter === 'mlb') && (
              <div className="smw-monitor-row">
                <span className="smw-sport-icon">⚾</span>
                <span className="smw-sport-label">MLB</span>
                <span className="smw-sport-value">
                  {lastResults.mlb_new_games > 0 ? (
                    <span className="smw-new">{lastResults.mlb_new_games} completed</span>
                  ) : (
                    <span className="smw-none">No new finals</span>
                  )}
                </span>
                {lastResults.mlb_errors?.length > 0 && (
                  <AlertTriangle size={12} className="smw-warn-icon" />
                )}
              </div>
              )}
            </div>
          )}

          {/* Check result (after manual trigger) */}
          {checkResult && !checkResult.error && (
            <div className="smw-check-result">
              <div className="smw-check-header">
                <CheckCircle size={13} />
                <span>Check completed at {new Date(checkResult.timestamp).toLocaleTimeString()}</span>
              </div>
              {(!sportFilter || sportFilter === 'nfl') && checkResult.nfl?.releases?.length > 0 && (
                <div className="smw-check-detail">
                  🏈 {checkResult.nfl.releases.map(r => `${r.repo.split('/')[1]}: ${r.tag}`).join(', ')}
                </div>
              )}
              {(!sportFilter || sportFilter === 'nba') && checkResult.nba?.games?.length > 0 && (
                <div className="smw-check-detail">
                  🏀 {checkResult.nba.games.map(g => g.matchup || g.game_id).join(', ')}
                </div>
              )}
              {(!sportFilter || sportFilter === 'mlb') && checkResult.mlb?.games?.length > 0 && (
                <div className="smw-check-detail">
                  ⚾ {checkResult.mlb.games.map(g => `${g.teams} (${g.score})`).join(', ')}
                </div>
              )}
              {checkResult.notifications?.length > 0 && (
                <div className="smw-check-detail smw-notif">
                  📨 Sent {checkResult.notifications.length} Discord notification{checkResult.notifications.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {checkResult?.error && (
            <div className="widget-body">
              <div className="widget-error">{checkResult.error}</div>
            </div>
          )}

          {/* Actions */}
          <div className="smw-actions">
            <button
              className="smw-check-btn"
              onClick={triggerCheck}
              disabled={checking}
            >
              {checking ? <RefreshCw size={13} className="spinning" /> : <Activity size={13} />}
              <span>{checking ? 'Checking...' : 'Run Check Now'}</span>
            </button>
            <button
              className="smw-refresh-btn"
              onClick={fetchStatus}
              disabled={loading}
            >
              <RefreshCw size={12} className={loading ? 'spinning' : ''} />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {/* Empty state */}
          {!lastResults && !error && !loading && (
            <div className="widget-body">
              <div className="widget-empty">No check data yet. Click "Run Check Now" to trigger.</div>
            </div>
          )}

          {loading && !lastResults && (
            <div className="widget-body">
              <div className="widget-empty">Loading status...</div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastCheck ? `Last check: ${new Date(lastCheck).toLocaleTimeString()}` : 'Never checked'}</span>
            <span>Cron: every 2h • {status?.state_entries || 0} tracked</span>
          </div>
        </>
      )}
    </div>
  )
}

export default StatsMonitorWidget
